import { z } from 'zod';
import { moneyMinorSchema, uuidSchema } from '../api';
import { PHARMACIST_SELECTION_REASONS } from '../add-ons-nphies';
import { ADMISSION_PRIORITIES } from './admission-priority';
import { ICD10_CODE_PATTERN, NPHIES_DISCHARGE_DISPOSITIONS, NPHIES_INTENDED_STAY_CODES } from './admission-stay-codes';
export const NPHIES_POLL_MODES = ['all', 'claim', 'preauth'];
export const nphiesPollNowSchema = z.object({
    mode: z.enum(NPHIES_POLL_MODES).default('all'),
});
export const POLL_CATCH_UP_MAX_DAYS = 7;
export const nphiesPollCatchUpSchema = z
    .object({
    from: z.string().date(),
    to: z.string().date(),
})
    .superRefine((value, ctx) => {
    const days = (Date.parse(value.to) - Date.parse(value.from)) / 86_400_000;
    if (days < 0)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'The end must not be before the start' });
    else if (days > POLL_CATCH_UP_MAX_DAYS)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: `Catch up at most ${POLL_CATCH_UP_MAX_DAYS} days at a time` });
});
export const INBOUND_AUTHORIZATION_STATUSES = ['open', 'linked', 'dismissed'];
export const inboundAuthorizationListQuerySchema = z.object({
    status: z.enum(INBOUND_AUTHORIZATION_STATUSES).default('open'),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    cursor: uuidSchema.optional(),
});
export const inboundAuthorizationLinkSchema = z.object({
    patientId: uuidSchema,
    coverageId: uuidSchema.optional(),
    overrideReason: z.string().trim().min(5).max(300).optional(),
});
export const inboundAuthorizationDismissSchema = z.object({
    reason: z.string().trim().min(3).max(300),
});
export const sourceKeySchema = z.string().regex(/^(invoice_item|clinical_order_item|lab_order_item|ot_case|prescription_item):[0-9a-fA-F-]{36}$/, 'That is not a billed or ordered line');
export const CLAIM_PRIORITIES = ['normal', 'deferred'];
export const claimSendSchema = z.object({
    includeRejectedAuth: z.array(sourceKeySchema).max(200).default([]),
    priority: z.enum(CLAIM_PRIORITIES).default('normal'),
});
export const SEND_READY_LIMIT = 100;
export const claimSendReadySchema = z.object({
    payerId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
});
export const deadLetterListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: uuidSchema.optional(),
});
export const communicationReplySchema = z.object({
    text: z.string().trim().min(3).max(2000),
});
export const communicationListQuerySchema = z.object({
    status: z.enum(['open', 'replied', 'closed', 'expired']).optional(),
    direction: z.enum(['inbound', 'outbound']).default('inbound'),
    sort: z.enum(['recent', 'due']).default('recent'),
    claimId: uuidSchema.optional(),
    preAuthId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    cursor: uuidSchema.optional(),
});
export const nphiesUnsolicitedSchema = z.object({
    aboutKind: z.enum(['claim', 'authorization']),
    aboutId: uuidSchema,
    claimItemSequence: z.number().int().positive().max(999).optional(),
    reasonCode: z.string().trim().regex(/^[A-Za-z0-9-]{1,40}$/, 'Not a reason code').optional(),
    text: z.string().trim().min(3).max(2000).optional(),
    attachmentIds: z.array(uuidSchema).max(5).default([]),
    send: z.boolean().default(true),
});
export const replyTemplateSchema = z.object({
    title: z.string().trim().min(2).max(120),
    body: z.string().trim().min(3).max(2000),
    isActive: z.boolean().default(true),
});
export const REPLY_TEMPLATE_LIMIT = 100;
export const COMMUNICATION_MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
export const COMMUNICATION_MAX_ATTACHMENTS = 5;
export const reconciliationListQuerySchema = z.object({
    status: z.enum(['received', 'posted', 'unmatched', 'needs_review']).optional(),
    claimId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    cursor: uuidSchema.optional(),
});
export const PRE_AUTH_TABS = ['pending', 'approved', 'rejected', 'failed', 'archived'];
export const preAuthListQuerySchema = z.object({
    tab: z.enum(PRE_AUTH_TABS).default('pending'),
    patientId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    cursor: uuidSchema.optional(),
});
export const preAuthExtendSchema = z
    .object({
    admissionId: uuidSchema,
    authFrom: z.string().datetime().optional(),
    authTo: z.string().datetime(),
    requestedAmountMinor: moneyMinorSchema.min(0).default(0),
    diagnosis: z.string().trim().max(600).optional(),
    procedure: z.string().trim().max(600).optional(),
    notes: z.string().max(2000).optional(),
})
    .refine((value) => !value.authFrom || new Date(value.authTo) >= new Date(value.authFrom), { message: 'The extension cannot end before it starts', path: ['authTo'] });
export const PRE_AUTH_SOURCE_TYPES = ['invoice_item', 'clinical_order_item', 'lab_order_item', 'ot_case', 'prescription_item'];
export const ORDER_SOURCE_TYPES = ['clinical_order_item', 'lab_order_item', 'ot_case', 'prescription_item'];
export const preAuthLineSelectionSchema = z.object({
    sourceType: z.enum(PRE_AUTH_SOURCE_TYPES).default('invoice_item'),
    sourceId: uuidSchema,
    quantity: z.number().positive().max(100000).optional(),
    resendReason: z.string().trim().min(5).max(300).optional(),
    daysSupply: z.number().int().min(1).max(365).optional(),
    selectionReason: z.enum(PHARMACIST_SELECTION_REASONS).optional(),
});
export const preAuthRequestSchema = z.object({
    patientId: uuidSchema,
    payerId: uuidSchema,
    admissionId: uuidSchema.optional(),
    encounterId: uuidSchema.optional(),
    prescriptionId: uuidSchema.optional(),
    diagnosis: z.string().trim().max(600).optional(),
    lines: z.array(preAuthLineSelectionSchema).max(200).optional(),
    resubmit: z.array(sourceKeySchema).max(200).default([]),
    send: z.boolean().optional(),
    includeOrders: z.boolean().optional(),
});
export const preAuthSourcesQuerySchema = z.object({
    patientId: uuidSchema,
    payerId: uuidSchema,
    admissionId: uuidSchema.optional(),
    encounterId: uuidSchema.optional(),
    prescriptionId: uuidSchema.optional(),
    include: z.enum(['orders']).optional(),
});
export const pharmacyWalkInPreAuthSchema = z.object({
    patientId: uuidSchema,
    payerId: uuidSchema,
    diagnosis: z.string().trim().min(3).max(600),
    lines: z
        .array(z.object({
        productId: uuidSchema,
        quantity: z.number().positive().max(10000),
        daysSupply: z.number().int().min(1).max(365),
        selectionReason: z.enum(PHARMACIST_SELECTION_REASONS).optional(),
    }))
        .min(1)
        .max(30),
    send: z.boolean().default(true),
});
export const integrationCodeGapsQuerySchema = z.object({
    localType: z.enum(['charge_item', 'product']).default('charge_item'),
    q: z
        .string()
        .trim()
        .max(60)
        .refine((value) => !/[\u0000-\u001f\u007f\ud800-\udfff]/.test(value), 'Remove the control or unreadable characters')
        .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    cursor: uuidSchema.optional(),
});
export const nphiesBranchLicenceSchema = z.object({
    providerLicense: z.string().trim().min(1).max(60).nullable().optional(),
    providerTypeCode: z.string().trim().min(1).max(40).nullable().optional(),
    locationLicense: z.string().trim().min(1).max(60).nullable().optional(),
    locationRoleCode: z.string().trim().min(1).max(40).nullable().optional(),
});
export const nphiesPracticeCodeSchema = z.object({
    practiceCode: z.string().trim().min(1).max(20).nullable().optional(),
});
export const NPHIES_VITAL_KEYS = ['systolic', 'diastolic', 'pulse', 'respiratoryRate', 'temperature', 'spo2', 'height', 'weight'];
export const nphiesAttachmentPickSchema = z.object({
    sourceRef: z.string().trim().min(10).max(80),
});
export const nphiesAbsenceReasonSchema = z.object({
    vital: z.enum(NPHIES_VITAL_KEYS),
    reason: z.enum(['888', '999']),
});
export const nphiesEstimatedStaySchema = z.object({
    days: z.number().int().min(0).max(365).nullable(),
});
export const NPHIES_SERVICE_EVENT_TYPES = ['ICSE', 'SCSE'];
export const NPHIES_READINESS_TARGETS = [
    'admission',
    'patient',
    'pre_authorization',
    'user',
    'specialty',
    'bed',
    'code_mapping',
    'branch',
    'integration_settings',
    'encounter',
    'lab_catalogue',
];
export const nphiesAdmissionDetailsSchema = z
    .object({
    admissionPriority: z.enum(ADMISSION_PRIORITIES).optional(),
    serviceEventType: z.enum(NPHIES_SERVICE_EVENT_TYPES).optional(),
    estimatedCostMinor: moneyMinorSchema.min(0).nullable().optional(),
    intendedStayCode: z.enum(NPHIES_INTENDED_STAY_CODES).optional(),
    dischargeDisposition: z.enum(NPHIES_DISCHARGE_DISPOSITIONS).optional(),
    dischargeSpecialtyId: uuidSchema.nullable().optional(),
    causeOfDeathCode: z.string().trim().toUpperCase().regex(ICD10_CODE_PATTERN, 'Pick the cause of death from the ICD-10 list').optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    path: ['admissionPriority'],
    message: 'Choose what to change',
});
export const upsertPreAuthRuleSchema = z
    .object({
    planId: uuidSchema.nullable().optional(),
    chargeCategory: z.string().trim().min(1).max(40).nullable().optional(),
    benefitCategory: z.string().trim().min(1).max(40).nullable().optional(),
    chargeItemId: uuidSchema.nullable().optional(),
    minAmountMinor: moneyMinorSchema.min(0).nullable().optional(),
    afterCount: z.number().int().min(0).max(1000).nullable().optional(),
    countPeriod: z.enum(['visit', 'year']).nullable().optional(),
    isActive: z.boolean().default(true),
    notes: z.string().trim().max(300).nullable().optional(),
})
    .refine((rule) => (rule.afterCount === null || rule.afterCount === undefined) === (rule.countPeriod === null || rule.countPeriod === undefined), {
    path: ['countPeriod'],
    message: 'Give both how many times and over what period',
});
export const manualWorklistQuerySchema = z.object({
    departmentId: uuidSchema.optional(),
    cursor: z.string().max(80).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
});
export const departmentPreauthModeSchema = z.object({
    mode: z.enum(['automatic', 'manual']).nullable(),
});
//# sourceMappingURL=nphies.js.map